import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Activity, Database, RefreshCw, ServerCog } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Combobox } from '@/components/ui/Combobox';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { useSyncStatus, useTriggerSync } from '@/hooks/useSyncStatus';
import { useHnfStatus } from '@/hooks/useHnf';
import { useUnmappedContracts } from '@/hooks/useContracts';
import {
  useDebouncedValue,
  useSetZabbixHostRef,
  useTriggerZabbixIngest,
  useZabbixAssignableSystems,
  useZabbixIntegrationStatus,
  useZabbixUnmatched,
  type ZabbixUnmatchedHost,
  type ZabbixUnmatchedReason,
} from '@/hooks/useZabbixIntegration';
import { formatDateTime, formatRelative } from '@/lib/format';

/**
 * Integrationen-Settings (Sprint 2, Zabbix seit S8-03).
 *
 * Karten:
 *   - Invoice Ninja Sync (last_run, next_run, items_fetched, mapping coverage)
 *   - HNF Mapping-DB (Connection-Status, total customers, mapped count)
 *   - Zabbix (Ingest-Status, Unmatched-Report mit Zuordnen/Ignorieren)
 */

export const Route = createFileRoute('/settings/integrations')({
  component: IntegrationsPage,
});

function IntegrationsPage(): JSX.Element {
  const sync = useSyncStatus();
  const trigger = useTriggerSync();
  const hnf = useHnfStatus();
  const unmapped = useUnmappedContracts();

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h1 className="text-2xl font-semibold">Integrationen</h1>
        <p className="text-muted">Status externer Systeme + manuelle Sync-Trigger.</p>
      </header>

      <Card
        title={
          <span className="inline-flex items-center gap-2">
            <Activity className="size-4" aria-hidden="true" />
            Invoice Ninja
          </span>
        }
        description="Recurring-Invoices-Sync (alle 6h)."
        actions={
          <Button
            leftIcon={<RefreshCw className="size-4" />}
            loading={trigger.isPending}
            onClick={() => trigger.mutate()}
          >
            Manueller Sync
          </Button>
        }
      >
        {sync.isError && <ErrorAlert error={sync.error} />}
        {trigger.isError && <ErrorAlert error={trigger.error} />}
        {sync.data && (
          <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted">Letzter Sync</dt>
              <dd className="font-medium">{formatDateTime(sync.data.last_run_at ?? null)}</dd>
              <dd className="text-xs text-muted">
                {sync.data.last_run_at && formatRelative(sync.data.last_run_at)}
              </dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted">Status</dt>
              <dd>
                <SyncStatusBadge status={sync.data.last_run_status ?? 'never'} />
              </dd>
              {sync.data.last_error && (
                <dd className="text-xs text-danger">{sync.data.last_error}</dd>
              )}
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted">Nächster Run</dt>
              <dd className="font-medium">{formatDateTime(sync.data.next_run_at ?? null)}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted">Items zuletzt</dt>
              <dd className="font-medium">
                {sync.data.items_fetched ?? 0} fetched ·{' '}
                {sync.data.items_updated ?? 0} updated
              </dd>
              {sync.data.duration_ms != null && (
                <dd className="text-xs text-muted">{Math.round(sync.data.duration_ms)}ms</dd>
              )}
            </div>
          </dl>
        )}
        <div className="mt-3 border-t border-border pt-3">
          <h3 className="text-sm font-semibold">Mapping-Coverage</h3>
          {unmapped.isError && <ErrorAlert error={unmapped.error} />}
          {unmapped.data && (
            <div className="mt-1 flex flex-wrap items-center gap-3 text-sm">
              <span>
                <strong>
                  {unmapped.data.total_contracts - unmapped.data.total_unmapped_contracts}
                </strong>{' '}
                / {unmapped.data.total_contracts} Vertraege gemappt (
                {unmapped.data.total_contracts > 0
                  ? Math.round(
                      ((unmapped.data.total_contracts -
                        unmapped.data.total_unmapped_contracts) /
                        unmapped.data.total_contracts) *
                        100
                    )
                  : 100}
                %)
              </span>
              {unmapped.data.total_unmapped_contracts > 0 && (
                <Link to="/settings/contracts/mapping">
                  <Button variant="secondary" size="sm">
                    Wizard öffnen
                  </Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </Card>

      <Card
        title={
          <span className="inline-flex items-center gap-2">
            <Database className="size-4" aria-hidden="true" />
            HNF Mapping-DB
          </span>
        }
        description="Bridge zu Kimai / Zammad / etc."
      >
        {hnf.isError && <ErrorAlert error={hnf.error} />}
        {hnf.data && (
          <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-3">
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted">Verbindung</dt>
              <dd>
                {hnf.data.connected ? (
                  <Badge tone="success">verbunden</Badge>
                ) : (
                  <Badge tone="danger">getrennt</Badge>
                )}
              </dd>
              {hnf.data.last_error && (
                <dd className="text-xs text-danger">{hnf.data.last_error}</dd>
              )}
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted">HNF-Customers</dt>
              <dd className="font-medium">{hnf.data.hnf_customers_total ?? '–'}</dd>
            </div>
            <div>
              <dt className="text-xs uppercase tracking-wider text-muted">Davon gemappt</dt>
              <dd className="font-medium">{hnf.data.mapped_count ?? '–'}</dd>
            </div>
          </dl>
        )}
      </Card>

      <ZabbixCard />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Zabbix (Spec S8-03)
// ---------------------------------------------------------------------------

const ZABBIX_REASON_LABELS: Record<ZabbixUnmatchedReason, string> = {
  no_candidate: 'kein Kandidat',
  ambiguous: 'mehrdeutig',
  excluded: 'ausgeschlossen',
  duplicate_target: 'mehrere Hosts auf ein System',
  no_supported_service: 'OS/Service nicht unterstützt',
};

function zabbixReasonTone(
  reason: ZabbixUnmatchedReason
): 'neutral' | 'warning' | 'danger' {
  if (reason === 'excluded') return 'neutral';
  if (reason === 'no_candidate') return 'warning';
  return 'danger';
}

function ZabbixCard(): JSX.Element {
  const status = useZabbixIntegrationStatus();
  const configured = status.data?.configured ?? false;
  const unmatched = useZabbixUnmatched({ enabled: configured });
  const trigger = useTriggerZabbixIngest();
  const setRef = useSetZabbixHostRef();

  const lastRun = status.data?.last_run ?? null;

  return (
    <Card
      title={
        <span className="inline-flex items-center gap-2">
          <ServerCog className="size-4" aria-hidden="true" />
          Zabbix
        </span>
      }
      description={`OS-/Kernel-Ingest aus dem Monitoring (alle ${
        status.data?.ingest_interval_hours ?? 6
      }h, read-only).`}
      actions={
        <Button
          leftIcon={<RefreshCw className="size-4" />}
          loading={trigger.isPending}
          disabled={!configured}
          onClick={() => trigger.mutate()}
        >
          Jetzt synchronisieren
        </Button>
      }
    >
      {status.isError && <ErrorAlert error={status.error} />}
      {trigger.isError && <ErrorAlert error={trigger.error} />}
      {status.data && !configured && (
        <p className="text-sm text-muted">
          Nicht konfiguriert — <code>ZABBIX_URL</code> und{' '}
          <code>ZABBIX_API_TOKEN</code> in <code>ops/.env</code> setzen.
        </p>
      )}
      {status.data && configured && (
        <dl className="grid grid-cols-2 gap-3 text-sm md:grid-cols-4">
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted">
              Letzter Ingest
            </dt>
            <dd className="font-medium">{formatDateTime(lastRun?.last_run ?? null)}</dd>
            <dd className="text-xs text-muted">
              {lastRun?.last_run && formatRelative(lastRun.last_run)}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted">Status</dt>
            <dd>
              {lastRun == null ? (
                <Badge tone="neutral">noch nie</Badge>
              ) : lastRun.errors.length > 0 ? (
                <Badge tone="danger">Fehler</Badge>
              ) : (
                <Badge tone="success">ok</Badge>
              )}
            </dd>
            {lastRun?.errors.map((err) => (
              <dd key={err} className="text-xs text-danger">
                {err}
              </dd>
            ))}
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted">
              Nächster Lauf
            </dt>
            <dd className="font-medium">
              {formatDateTime(status.data.next_run_at ?? null)}
            </dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wider text-muted">
              Hosts zuletzt
            </dt>
            <dd className="font-medium">
              {lastRun ? (
                <>
                  {lastRun.hosts_seen} gesehen · {lastRun.matched} zugeordnet ·{' '}
                  {lastRun.unmatched_count} offen
                </>
              ) : (
                '–'
              )}
            </dd>
            {lastRun?.observations_written != null && (
              <dd className="text-xs text-muted">
                {lastRun.observations_written} Observations geschrieben
              </dd>
            )}
          </div>
        </dl>
      )}

      {configured && (
        <div className="mt-3 border-t border-border pt-3">
          <h3 className="text-sm font-semibold">Nicht zugeordnete Zabbix-Hosts</h3>
          <p className="text-xs text-muted">
            Zuordnen setzt das Override <code>zabbix_host_ref</code> am System;
            Ignorieren schliesst das gewaehlte System per Leerstring vom Ingest
            aus.
          </p>
          {unmatched.isError && <ErrorAlert error={unmatched.error} />}
          {unmatched.data && unmatched.data.hosts.length === 0 && (
            <p className="mt-2 text-sm text-muted">
              Alle Zabbix-Hosts sind einem System zugeordnet.
            </p>
          )}
          {unmatched.data && unmatched.data.hosts.length > 0 && (
            <ul className="mt-2 flex flex-col gap-2">
              {unmatched.data.hosts.map((host) => (
                <ZabbixUnmatchedRow
                  key={host.zabbix_hostid}
                  host={host}
                  onAssign={(systemId) =>
                    setRef.mutate({ systemId, zabbixHostRef: host.zabbix_host })
                  }
                  onIgnore={(systemId) =>
                    setRef.mutate({ systemId, zabbixHostRef: '' })
                  }
                  pending={setRef.isPending}
                />
              ))}
            </ul>
          )}
          {setRef.isError && <ErrorAlert error={setRef.error} />}

          {unmatched.data && unmatched.data.orphaned_overrides.length > 0 && (
            <div className="mt-3">
              <h3 className="text-sm font-semibold">Verwaiste Overrides</h3>
              <p className="text-xs text-muted">
                Diese Systeme verweisen auf einen Zabbix-Host, den es nicht
                (mehr) gibt — sie bekommen keine Observations.
              </p>
              <ul className="mt-1 flex flex-col gap-1">
                {unmatched.data.orphaned_overrides.map((o) => (
                  <li
                    key={o.system_id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border px-3 py-2 text-sm"
                  >
                    <span>
                      <strong>{o.label}</strong>{' '}
                      <span className="font-mono text-xs text-muted">
                        → {o.zabbix_host_ref}
                      </span>
                    </span>
                    <Button
                      variant="secondary"
                      size="sm"
                      disabled={setRef.isPending}
                      onClick={() =>
                        setRef.mutate({
                          systemId: o.system_id,
                          zabbixHostRef: null,
                        })
                      }
                      title="Override entfernen — System wird wieder automatisch zugeordnet."
                    >
                      Auf Auto-Match zurücksetzen
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

interface ZabbixUnmatchedRowProps {
  host: ZabbixUnmatchedHost;
  onAssign: (systemId: string) => void;
  onIgnore: (systemId: string) => void;
  pending: boolean;
}

export function ZabbixUnmatchedRow({
  host,
  onAssign,
  onIgnore,
  pending,
}: ZabbixUnmatchedRowProps): JSX.Element {
  // Vorauswahl: bester Vorschlag des Backends (hostname/ip-Aehnlichkeit).
  // Wert + Label werden gemerkt, damit die Auswahl sichtbar bleibt, auch wenn
  // die Option nach einer neuen Suche nicht mehr in der Liste steht.
  const [selected, setSelected] = useState<{ value: string; label: string } | null>(
    host.suggestions[0]
      ? { value: host.suggestions[0].system_id, label: host.suggestions[0].label }
      : null
  );
  // Serverseitige Suche (Review-Fix S8-03): statt einer fixen 200er-Liste
  // fragt die Combobox list_systems mit filter[search] ab (debounced);
  // excluded-Systeme filtert der Hook aus (der Matcher lädt sie nie).
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 300);
  const assignable = useZabbixAssignableSystems(debouncedSearch);

  const q = search.trim().toLowerCase();
  const suggestionIds = new Set(host.suggestions.map((s) => s.system_id));
  const options = [
    // Vorschläge clientseitig auf die Eingabe gefiltert (sie sind schon da).
    ...host.suggestions
      .filter((s) => !q || s.label.toLowerCase().includes(q))
      .map((s) => ({
        value: s.system_id,
        label: s.label,
        hint:
          s.score != null ? `Vorschlag · ${Math.round(s.score * 100)}%` : 'Vorschlag',
      })),
    ...assignable.options.filter((o) => !suggestionIds.has(o.value)),
  ];
  // Aktuelle Auswahl in der Liste halten (sonst zeigt das Input nichts an).
  if (selected && !options.some((o) => o.value === selected.value)) {
    options.push({ value: selected.value, label: selected.label });
  }

  return (
    <li className="flex flex-wrap items-end gap-2 rounded-md border border-border px-3 py-2">
      <div className="min-w-40 flex-1">
        <div className="text-sm font-medium font-mono">{host.zabbix_host}</div>
        <div className="text-xs text-muted">
          {host.zabbix_name !== host.zabbix_host && `${host.zabbix_name} · `}
          {host.ips.join(', ') || 'keine IP'}
        </div>
        <div className="mt-1">
          <Badge tone={zabbixReasonTone(host.reason)}>
            {ZABBIX_REASON_LABELS[host.reason]}
          </Badge>
        </div>
      </div>
      <div className="min-w-56 flex-1">
        <Combobox
          label=""
          hideLabel
          ariaLabel={`System für ${host.zabbix_host} wählen`}
          value={selected?.value ?? null}
          onChange={(value) => {
            if (value == null) {
              setSelected(null);
              return;
            }
            const opt = options.find((o) => o.value === value);
            setSelected({ value, label: opt?.label ?? value });
          }}
          options={options}
          // Der Server filtert (filter[search] matcht hostname UND
          // display_name) - der eingebaute Label-Substring-Filter würde
          // Treffer fälschlich wieder verstecken.
          filterOptions={false}
          onQueryChange={setSearch}
          footerHint={
            assignable.truncated
              ? 'Weitere Treffer vorhanden - Suche verfeinern.'
              : undefined
          }
          emptyText={
            assignable.isLoading ? 'Systeme werden geladen ...' : 'Keine Treffer.'
          }
          placeholder="System suchen ..."
        />
      </div>
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          disabled={!selected || pending}
          onClick={() => selected && onAssign(selected.value)}
          title="Setzt zabbix_host_ref des gewählten Systems auf diesen Zabbix-Host."
        >
          Zuordnen
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!selected || pending}
          onClick={() => selected && onIgnore(selected.value)}
          title="Schließt das gewählte System per Leerstring vom Zabbix-Ingest aus."
        >
          Ignorieren
        </Button>
      </div>
    </li>
  );
}

function SyncStatusBadge({ status }: { status: 'ok' | 'partial' | 'failed' | 'never' }): JSX.Element {
  switch (status) {
    case 'ok':
      return <Badge tone="success">ok</Badge>;
    case 'partial':
      return <Badge tone="warning">partial</Badge>;
    case 'failed':
      return <Badge tone="danger">failed</Badge>;
    default:
      return <Badge tone="neutral">noch nie</Badge>;
  }
}
